import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FAILURE_TYPES,
  createQuarantineBin,
  listDamageReports,
  listQuarantineBins,
  reclassifyDamageReport,
  type PartFailure,
} from '@/lib/api/warranty.js';
import { listUsers } from '@/lib/api/users.js';
import { SupplierSelect } from '@/components/suppliers/SupplierSelect.js';
import { formatCurrency, formatDate, prettyLabel } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { isManagerRole } from '@/lib/permissions.js';
import { useAuthStore } from '@/stores/auth.js';

export function DamageReportsPage() {
  const role = useAuthStore((s) => s.user?.role ?? 'read_only');
  const canReview = isManagerRole(role);
  const queryClient = useQueryClient();
  const [reviewed, setReviewed] = useState('unreviewed');
  const [disposition, setDisposition] = useState('');
  const [failureType, setFailureType] = useState('');
  const [attributedTo, setAttributedTo] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [target, setTarget] = useState<PartFailure | null>(null);
  const [nextDisposition, setNextDisposition] = useState<'quarantine' | 'rma' | 'write_off'>('rma');
  const [note, setNote] = useState('');
  const [binName, setBinName] = useState('');
  const [binSupplier, setBinSupplier] = useState('');

  const reportsQuery = useQuery({
    queryKey: ['damage-reports', reviewed, disposition, failureType, attributedTo, dateFrom, dateTo],
    queryFn: () =>
      listDamageReports({
        reviewed,
        disposition: disposition || undefined,
        failureType: failureType || undefined,
        attributedTo: attributedTo || undefined,
        dateFrom: dateFrom ? new Date(dateFrom).toISOString() : undefined,
        dateTo: dateTo ? new Date(`${dateTo}T23:59:59`).toISOString() : undefined,
      }),
  });
  const binsQuery = useQuery({ queryKey: ['quarantine-bins'], queryFn: listQuarantineBins });
  const usersQuery = useQuery({ queryKey: ['users'], queryFn: listUsers });

  const reviewMutation = useMutation({
    mutationFn: () => reclassifyDamageReport(target!.id, { disposition: nextDisposition, note }),
    onSuccess: () => {
      toastSuccess('Disposition updated');
      setTarget(null);
      setNote('');
      void queryClient.invalidateQueries({ queryKey: ['damage-reports'] });
      void queryClient.invalidateQueries({ queryKey: ['quarantine-summary'] });
    },
    onError: (error) => toastError(parseApiError(error).message),
  });

  const binMutation = useMutation({
    mutationFn: () => createQuarantineBin({ name: binName, supplierId: binSupplier || null }),
    onSuccess: () => {
      toastSuccess('Quarantine bin added');
      setBinName('');
      void queryClient.invalidateQueries({ queryKey: ['quarantine-bins'] });
    },
    onError: (error) => toastError(parseApiError(error).message),
  });

  const rows = reportsQuery.data?.data ?? [];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-slate-950">Damage reports</h1>
        <p className="text-sm text-slate-500">
          Parts leave sellable stock as soon as they are reported. A manager can reclassify them afterward.
        </p>
      </div>

      <div className="rounded-2xl border bg-white p-4 shadow-sm">
        <h2 className="font-semibold text-slate-950">Quarantine bins</h2>
        <ul className="mt-2 space-y-1 text-sm text-slate-700">
          {(binsQuery.data ?? []).map((bin) => (
            <li key={bin.id}>
              {bin.name}
              {bin.supplierName ? ` · ${bin.supplierName}` : ''}
              {bin.location ? ` · ${bin.location}` : ''}
              {!bin.active ? ' · inactive' : ''}
            </li>
          ))}
          {(binsQuery.data ?? []).length === 0 ? <li className="text-slate-500">No bins yet. One bin or one per supplier both work.</li> : null}
        </ul>
        {canReview ? (
          <div className="mt-3 flex flex-wrap gap-2">
            <input
              className="rounded-xl border px-3 py-2 text-sm"
              placeholder="Bin name"
              value={binName}
              onChange={(event) => setBinName(event.target.value)}
            />
            <div className="min-w-[200px]">
              <SupplierSelect value={binSupplier} onChange={setBinSupplier} />
            </div>
            <button
              type="button"
              className="rounded-xl border px-3 py-2 text-sm font-semibold"
              disabled={!binName.trim() || binMutation.isPending}
              onClick={() => binMutation.mutate()}
            >
              Add bin
            </button>
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2">
        <select className="rounded-xl border bg-white px-3 py-2 text-sm" value={reviewed} onChange={(e) => setReviewed(e.target.value)}>
          <option value="unreviewed">Unreviewed</option>
          <option value="reviewed">Reviewed</option>
          <option value="all">All</option>
        </select>
        <select className="rounded-xl border bg-white px-3 py-2 text-sm" value={disposition} onChange={(e) => setDisposition(e.target.value)}>
          <option value="">Any disposition</option>
          <option value="quarantine">Quarantine</option>
          <option value="rma">RMA</option>
          <option value="write_off">Write off</option>
          <option value="returned_to_stock">Returned to stock</option>
        </select>
        <select className="rounded-xl border bg-white px-3 py-2 text-sm" value={failureType} onChange={(e) => setFailureType(e.target.value)}>
          <option value="">Any failure</option>
          {FAILURE_TYPES.map((type) => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>
        <select className="rounded-xl border bg-white px-3 py-2 text-sm" value={attributedTo} onChange={(e) => setAttributedTo(e.target.value)}>
          <option value="">Any technician</option>
          {(usersQuery.data ?? []).map((user) => (
            <option key={user.id} value={user.id}>{user.firstName} {user.lastName}</option>
          ))}
        </select>
        <input type="date" className="rounded-xl border px-3 py-2 text-sm" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        <input type="date" className="rounded-xl border px-3 py-2 text-sm" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
      </div>

      <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3">Part</th>
              <th className="px-4 py-3">Failure</th>
              <th className="px-4 py-3">Disposition</th>
              <th className="px-4 py-3">Cost</th>
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((row) => (
              <tr key={row.id}>
                <td className="px-4 py-3">
                  <div className="font-medium">{row.partName}</div>
                  <div className="text-xs text-slate-500">
                    {row.supplierName ?? 'No supplier'}
                    {row.attributedToName ? ` · ${row.attributedToName}` : ''}
                    {row.warrantyTicketNumber ? ` · ${row.warrantyTicketNumber}` : ''}
                  </div>
                </td>
                <td className="px-4 py-3">{prettyLabel(row.failureType)}</td>
                <td className="px-4 py-3">{prettyLabel(row.disposition)}</td>
                <td className="px-4 py-3">{formatCurrency(row.unitCost)}</td>
                <td className="px-4 py-3">{formatDate(row.failedAt)}</td>
                <td className="px-4 py-3 text-right">
                  {canReview ? (
                    <button
                      type="button"
                      className="text-sm font-semibold text-primary"
                      onClick={() => {
                        setTarget(row);
                        setNextDisposition(row.disposition === 'write_off' ? 'rma' : 'write_off');
                        setNote('');
                      }}
                    >
                      Reclassify
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
            {!reportsQuery.isLoading && rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-500">No damage reports match these filters.</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {target ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
          <form
            className="w-full max-w-md space-y-3 rounded-2xl bg-white p-6 shadow-2xl"
            onSubmit={(event) => {
              event.preventDefault();
              reviewMutation.mutate();
            }}
          >
            <h3 className="text-lg font-semibold">Reclassify {target.partName}</h3>
            <p className="text-sm text-slate-600">
              Currently {prettyLabel(target.disposition)}. Changing this does not put the part back on the shelf unless you return it to stock later.
            </p>
            <select
              className="w-full rounded-xl border px-3 py-2"
              value={nextDisposition}
              onChange={(event) => setNextDisposition(event.target.value as typeof nextDisposition)}
            >
              <option value="quarantine">Quarantine</option>
              <option value="rma">RMA</option>
              <option value="write_off">Write off</option>
            </select>
            <textarea
              required
              className="w-full rounded-xl border px-3 py-2"
              placeholder="Why this disposition"
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button type="button" className="rounded-xl border px-4 py-2 text-sm font-semibold" onClick={() => setTarget(null)}>
                Cancel
              </button>
              <button type="submit" className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white" disabled={reviewMutation.isPending}>
                Save
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}
